import {
  EQUIPMENT_WAITING,
  EQUIPMENT_WAITING_LABELS,
  type EquipmentDetails,
  type EquipmentSort,
  type EquipmentWaiting,
  type ProductionStageItem,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { ChevronLeft, FileText, Plus, Search, Trash2, Truck } from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';
import { Comments } from '@/components/comments';
import { EmptyPanel, Pager } from '@/components/crm-bits';
import {
  EquipmentNav,
  ExpectedDate,
  HistoryPanel,
  LifecycleBadge,
  Person,
  Row,
} from '@/components/equipment-bits';
import {
  clientOf,
  EquipmentFormDialog,
  ReasonDialog,
  ShipDialog,
  TicketFormDialog,
} from '@/components/equipment-forms';
import { EQUIPMENT_COLUMNS, EquipmentTable, TicketTable, Warranty } from '@/components/equipment-tables';
import { ColumnsMenu, ExportButton, useColumns } from '@/components/list-tools';
import { Button, ErrorBox, Input, PageHeader, Segmented, Select, Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import { useCan } from '@/lib/auth';
import {
  type EquipmentFilter,
  equipmentHistoryQuery,
  equipmentListQuery,
  equipmentMetaQuery,
  equipmentQuery,
  ticketsQuery,
} from '@/lib/equipment';
import { formatDay, formatMoment, plural } from '@/lib/format';

export interface EquipmentSearch {
  lifecycle?: 'production' | 'decommissioned' | 'all';
  model?: string;
  q?: string;
  waiting?: EquipmentWaiting;
  sort?: EquipmentSort;
  dir?: 'asc' | 'desc';
  page?: number;
}

export function EquipmentListPage() {
  const search = useSearch({ strict: false }) as EquipmentSearch;
  const navigate = useNavigate();
  const can = useCan();
  const meta = useQuery(equipmentMetaQuery);
  const [q, setQ] = useState(search.q ?? '');
  const [creating, setCreating] = useState<'production' | 'in_service' | null>(null);
  const debounce = useRef<number | undefined>(undefined);
  const lifecycle = search.lifecycle ?? 'in_service';
  const filter: EquipmentFilter = {
    lifecycle,
    modelId: search.model,
    q: search.q,
    waiting: search.waiting,
    sort: search.sort,
    dir: search.dir,
  };
  const list = useQuery(equipmentListQuery({ ...filter, page: search.page ?? 1 }));
  const cols = useColumns('equipment', EQUIPMENT_COLUMNS);
  const setSearch = (patch: Partial<EquipmentSearch>) =>
    navigate({ to: '/equipment', search: { ...search, page: undefined, ...patch }, replace: true });
  const onQuery = (value: string) => {
    setQ(value);
    window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => setSearch({ q: value.trim() || undefined }), 250);
  };

  return (
    <div>
      <PageHeader
        title="База оборудования"
        description="Стенды у клиентов: договор, гарантия, история сборки и обращений"
        actions={
          <>
            {can('equipment.manage') && (
              <Button variant="secondary" onClick={() => setCreating('in_service')}>
                <Plus className="size-4" aria-hidden />
                Оборудование клиента
              </Button>
            )}
            {can('production.manage') && (
              <Button onClick={() => setCreating('production')}>
                <Plus className="size-4" aria-hidden />
                Стенд в производство
              </Button>
            )}
          </>
        }
      />
      <EquipmentNav />
      {creating && <EquipmentFormDialog mode={creating} onClose={() => setCreating(null)} />}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Segmented
          label="Состояние"
          value={lifecycle}
          onChange={(v) => setSearch({ lifecycle: v === 'in_service' ? undefined : v })}
          options={[
            { value: 'in_service', label: 'У клиентов' },
            { value: 'production', label: 'В производстве' },
            { value: 'decommissioned', label: 'Списано' },
            { value: 'all', label: 'Всё' },
          ]}
        />
        <Select
          aria-label="Модель"
          value={search.model ?? ''}
          onChange={(e) => setSearch({ model: e.target.value || undefined })}
          className="w-[190px]"
        >
          <option value="">Все модели</option>
          {meta.data?.models.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </Select>
        <div className="relative w-full max-w-[300px]">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-steel"
            aria-hidden
          />
          <Input
            type="search"
            value={q}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Заводской №, плата, клиент, город"
            aria-label="Поиск оборудования"
            className="pl-9"
          />
        </div>
        <Select
          aria-label="Ожидание деталей"
          value={search.waiting ?? ''}
          onChange={(e) =>
            setSearch({ waiting: (e.target.value || undefined) as EquipmentWaiting | undefined })
          }
          className="w-[210px]"
        >
          <option value="">Детали — любые</option>
          {EQUIPMENT_WAITING.map((w) => (
            <option key={w} value={w}>
              {EQUIPMENT_WAITING_LABELS[w]}
            </option>
          ))}
        </Select>
        <ColumnsMenu state={cols} />
        <ExportButton path="/equipment/export.xlsx" query={{ ...filter }} />
      </div>
      {list.isPending ? (
        <Spinner />
      ) : list.error ? (
        <ErrorBox error={list.error} />
      ) : list.data.items.length === 0 ? (
        <EmptyPanel title="Ничего не найдено">Измените фильтры или внесите оборудование.</EmptyPanel>
      ) : (
        <>
          <EquipmentTable
            items={list.data.items}
            sort={{ sort: search.sort, dir: search.dir }}
            onSort={(st) => setSearch({ sort: st.sort, dir: st.sort ? st.dir : undefined })}
            cols={cols}
          />
          <Pager
            page={list.data.page}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => setSearch({ page: p })}
          />
        </>
      )}
    </div>
  );
}

// ---------- Карточка ----------

export function EquipmentPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const qc = useQueryClient();
  const can = useCan();
  const q = useQuery(equipmentQuery(id));
  const meta = useQuery(equipmentMetaQuery);
  const history = useQuery(equipmentHistoryQuery(id));
  const tickets = useQuery({ ...ticketsQuery({ equipmentId: id, state: 'all' }), enabled: Boolean(q.data) });
  const [dialog, setDialog] = useState<'edit' | 'ship' | 'ticket' | 'decommission' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const move = useMutation({
    mutationFn: (stageId: string) => api(`/equipment/${id}/move`, { method: 'POST', body: { stageId } }),
    onSuccess: () => setError(null),
    onError: (err) => setError(err.message),
    onSettled: () => qc.invalidateQueries({ queryKey: ['equipment'] }),
  });
  const decommission = useMutation({
    mutationFn: (reason: string) =>
      api(`/equipment/${id}/decommission`, { method: 'POST', body: { reason } }),
    onSuccess: () => setDialog(null),
    onSettled: () => qc.invalidateQueries({ queryKey: ['equipment'] }),
  });

  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  const e = q.data;
  const crm = can('crm.access');
  const ready = e.stage?.kind === 'ready';

  return (
    <div className="max-w-[1240px]">
      <Link
        to={e.lifecycle === 'production' && can('production.manage') ? '/production' : '/equipment'}
        className="mb-4 inline-flex items-center gap-1 text-[13px] text-steel hover:text-ink"
      >
        <ChevronLeft className="size-4" aria-hidden />
        {e.lifecycle === 'production' && can('production.manage') ? 'Производство' : 'База оборудования'}
      </Link>
      <header className="mb-4 flex flex-wrap items-start gap-3">
        <div className="min-w-[240px] flex-1">
          <p className="flex flex-wrap items-center gap-2 text-[13px] text-steel">
            <span className="tabular-nums">Заводской № {e.serialNo}</span>
            <LifecycleBadge lifecycle={e.lifecycle} />
          </p>
          <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.015em]">{e.title}</h1>
          {(e.contact || e.company) && (
            <p className="mt-0.5 text-ink-soft">
              {[e.contact?.fullName, e.company?.name].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {e.permissions.canProduce && ready && (
            <Button onClick={() => setDialog('ship')}>
              <Truck className="size-4" aria-hidden />
              Отгрузить
            </Button>
          )}
          {e.permissions.canCreateTicket && e.lifecycle !== 'production' && (
            <Button
              variant={e.permissions.canProduce ? 'secondary' : 'primary'}
              onClick={() => setDialog('ticket')}
            >
              <Plus className="size-4" aria-hidden />
              Сервисная заявка
            </Button>
          )}
          {e.permissions.canEdit && (
            <Button variant="secondary" onClick={() => setDialog('edit')}>
              Изменить
            </Button>
          )}
        </div>
      </header>

      {dialog === 'edit' && <EquipmentFormDialog equipment={e} onClose={() => setDialog(null)} />}
      {dialog === 'ship' && <ShipDialog equipment={e} onClose={() => setDialog(null)} />}
      {dialog === 'ticket' && (
        <TicketFormDialog
          defaults={{ equipment: { id: e.id, label: e.title }, client: clientOf(e) }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'decommission' && (
        <ReasonDialog
          title={`Списать ${e.title}`}
          label="Причина"
          placeholder="Клиент сдал в утиль, продан третьим лицам…"
          submitLabel="Списать"
          loading={decommission.isPending}
          error={decommission.error?.message}
          onSubmit={(reason) => decommission.mutate(reason)}
          onClose={() => setDialog(null)}
        />
      )}

      {e.lifecycle === 'production' && meta.data && (
        <div className="mb-6">
          <StageButtons
            stages={meta.data.stages}
            current={e.stage?.id ?? null}
            disabled={!e.permissions.canProduce || move.isPending}
            onPick={(s) => move.mutate(s.id)}
          />
          {error && (
            <p role="alert" className="mt-2 text-[13.5px] text-danger">
              {error}
            </p>
          )}
          {e.stage && (
            <p className="mt-2 text-[12.5px] text-steel">
              На этапе «{e.stage.name}» с {formatMoment(e.stageChangedAt)}
            </p>
          )}
        </div>
      )}

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-6">
          {(e.lifecycle !== 'production' || Boolean(tickets.data?.total)) && (
            <section>
              <h2 className="mb-2 font-semibold">
                Сервисные заявки
                {tickets.data && tickets.data.total > 0 && (
                  <span className="ml-2 text-[13px] font-normal text-steel">
                    {tickets.data.total} {plural(tickets.data.total, ['заявка', 'заявки', 'заявок'])}
                  </span>
                )}
              </h2>
              {tickets.data?.items.length ? (
                <TicketTable items={tickets.data.items} compact />
              ) : (
                <p className="panel px-4 py-3 text-[13.5px] text-steel">
                  Обращений по этому оборудованию не было.
                </p>
              )}
            </section>
          )}
          {e.notes && (
            <section className="panel px-5 py-4">
              <h2 className="mb-1.5 font-semibold">Примечание</h2>
              <p className="max-w-[75ch] whitespace-pre-wrap break-words">{e.notes}</p>
            </section>
          )}
          <Comments entityType="equipment" entityId={e.id} />
        </div>
        <aside className="flex flex-col gap-6">
          <Details e={e} crm={crm} />
          <HistoryPanel items={history.data} />
          <div className="flex flex-col items-start gap-2">
            {can('equipment.manage') && e.lifecycle === 'in_service' && (
              <button
                type="button"
                onClick={() => setDialog('decommission')}
                className="text-[13px] text-steel hover:text-danger"
              >
                Списать оборудование
              </button>
            )}
            {e.permissions.canDelete && <DeleteEquipment e={e} />}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Details({ e, crm }: { e: EquipmentDetails; crm: boolean }) {
  const items: [string, ReactNode][] = [];
  const client = e.contact ? (
    crm ? (
      <Link to="/crm/contacts/$id" params={{ id: e.contact.id }} className="link">
        {e.contact.fullName}
      </Link>
    ) : (
      e.contact.fullName
    )
  ) : null;
  const company = e.company ? (
    crm ? (
      <Link to="/crm/companies/$id" params={{ id: e.company.id }} className="link">
        {e.company.name}
      </Link>
    ) : (
      e.company.name
    )
  ) : null;
  items.push(['Модель', e.model?.name ?? e.name ?? '—']);
  items.push(['ID платы', e.boardId ?? '—']);
  if (client || company)
    items.push([
      'Клиент',
      <span key="c" className="flex flex-col">
        {client && <span>{client}</span>}
        {company && <span>{company}</span>}
      </span>,
    ]);
  if (e.deal)
    items.push([
      'Сделка',
      crm ? (
        <Link key="d" to="/crm/deals/$id" params={{ id: e.deal.id }} className="link">
          #{e.deal.number} {e.deal.title}
        </Link>
      ) : (
        `#${e.deal.number} ${e.deal.title}`
      ),
    ]);
  items.push(['Сборщик', <Person key="a" user={e.assembler} />]);
  if (e.lifecycle === 'production') {
    items.push([
      'Комплектующие',
      e.caseExpectedAt || e.boardExpectedAt ? (
        <span key="x" className="flex flex-col">
          <ExpectedDate label="Корпус" date={e.caseExpectedAt} assembled={Boolean(e.assembledAt)} />
          <ExpectedDate label="Плата" date={e.boardExpectedAt} assembled={Boolean(e.assembledAt)} />
        </span>
      ) : (
        <span key="x" className="text-steel">
          сроки не указаны
        </span>
      ),
    ]);
  }
  items.push(['Окончание сборки', e.assembledAt ? formatDay(e.assembledAt) : '—']);
  if (e.lifecycle !== 'production') {
    items.push(['Отгрузка', e.shippedAt ? formatDay(e.shippedAt) : '—']);
    items.push(['Гарантия до', <Warranty key="w" until={e.warrantyUntil} />]);
    items.push(['Где стоит', e.location ?? '—']);
    if (e.inventoryNo) items.push(['Инв. № клиента', e.inventoryNo]);
  }
  items.push([
    'Договор',
    e.contractFile ? (
      <a
        key="f"
        href={e.contractFile.url}
        className="link inline-flex items-center gap-1.5"
        target="_blank"
        rel="noreferrer"
      >
        <FileText className="size-3.5" aria-hidden />
        {e.contractFile.name}
      </a>
    ) : (
      <span key="f" className="text-steel">
        не прикреплён
      </span>
    ),
  ]);
  items.push([
    'Внесён',
    <span key="cr">
      {formatMoment(e.createdAt)}
      {e.createdBy && <span className="text-steel"> · {e.createdBy.fullName}</span>}
    </span>,
  ]);
  return (
    <section className="panel px-5 py-4">
      <dl className="flex flex-col gap-3.5">
        {items.map(([k, v]) => (
          <Row key={k} label={k}>
            {v}
          </Row>
        ))}
      </dl>
    </section>
  );
}

/** Этапы сборки кнопками — работает и на телефоне, где нет перетаскивания. */
function StageButtons({
  stages,
  current,
  onPick,
  disabled,
}: {
  stages: ProductionStageItem[];
  current: string | null;
  onPick: (s: ProductionStageItem) => void;
  disabled: boolean;
}) {
  const work = stages.filter((s) => s.kind !== 'paused');
  const paused = stages.filter((s) => s.kind === 'paused');
  const idx = work.findIndex((s) => s.id === current);
  return (
    <fieldset className="flex min-w-0 flex-wrap gap-1.5">
      <legend className="sr-only">Этап сборки</legend>
      <div className="flex w-full min-w-0 flex-wrap gap-1 sm:flex-1 sm:flex-nowrap">
        {work.map((s, i) => {
          const passed = idx >= 0 && i <= idx;
          const isCurrent = s.id === current;
          return (
            <button
              key={s.id}
              type="button"
              disabled={disabled || isCurrent}
              onClick={() => onPick(s)}
              aria-pressed={isCurrent}
              title={s.name}
              className={clsx(
                'h-8 min-w-0 flex-1 basis-[30%] truncate rounded px-2 text-[12.5px] font-medium transition-colors sm:basis-0',
                passed ? 'text-white' : 'bg-sunken text-ink-soft hover:bg-line',
                isCurrent && 'ring-2 ring-signal ring-offset-1',
                disabled && !isCurrent && 'cursor-default hover:bg-sunken',
              )}
              style={passed ? { background: s.color } : undefined}
            >
              {s.name}
            </button>
          );
        })}
      </div>
      {paused.map((s) => {
        const isCurrent = s.id === current;
        return (
          <button
            key={s.id}
            type="button"
            disabled={disabled || isCurrent}
            onClick={() => onPick(s)}
            aria-pressed={isCurrent}
            className={clsx(
              'inline-flex h-8 items-center rounded border px-2.5 text-[12.5px] font-medium',
              isCurrent
                ? 'border-transparent text-white'
                : 'border-line-strong bg-surface text-ink-soft hover:bg-sunken',
            )}
            style={isCurrent ? { background: s.color } : undefined}
          >
            {s.name}
          </button>
        );
      })}
    </fieldset>
  );
}

function DeleteEquipment({ e }: { e: EquipmentDetails }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const del = useMutation({
    mutationFn: () => api(`/equipment/${e.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['equipment'] });
      await navigate({ to: e.lifecycle === 'production' ? '/production' : '/equipment' });
    },
  });
  return confirm ? (
    <div className="panel flex w-full flex-col gap-2 px-5 py-4">
      <p className="text-[13.5px]">
        Удалить {e.title}? Запись пропадёт из базы, заявки останутся. Если стенд просто сдан в утиль — лучше
        «Списать».
      </p>
      <div className="flex gap-2">
        <Button variant="danger" size="sm" loading={del.isPending} onClick={() => del.mutate()}>
          Удалить
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setConfirm(false)}>
          Отмена
        </Button>
      </div>
    </div>
  ) : (
    <button
      type="button"
      onClick={() => setConfirm(true)}
      className="inline-flex items-center gap-1.5 text-[13px] text-steel hover:text-danger"
    >
      <Trash2 className="size-3.5" aria-hidden />
      Удалить запись
    </button>
  );
}
